namespace e5 {
    export interface IScrollbarBehaviour {
        getIndex(scrollbar: ScrollbarListDynamic): number;
        fromView(scrollbar: ScrollbarListDynamic): ScrollbarData;
    }

    export class ScrollbarListDynamic<Data extends IListData = any> extends Scrollbar {
        public readonly view: ListDynamic<Data>;
        public labelProvider: (data: Data) => string = null;
        public behaviour: IScrollbarBehaviour = ScrollbarBehaviour.INDEX;

        private _namesapce: string = Signal.getUniqueNamespace();
        private _label: HTMLDivElement;
        private _labelItem: ListItem<Data, IListVisual> = null;

        constructor(view: ListDynamic<Data>, theme: ScrollbarTheme = "lite") {
            super(false, theme)

            this.view = view;

            this._label = document.createElement("div");
            this._label.className = "e5-scrollbar-label hidden";
            this.element.appendChild(this._label);

            //Re-dispatch mouse wheel event to ListDynamic.
            this.element.addEventListener("wheel", (e) => {
                if (!this.view || !this.view.scroller)
                    return;
                if (this.dragger.pointers.length === 0) {
                    try {
                        this.view.scroller.dispatchEvent(new WheelEvent(e.type, e));
                    } catch (error) {
                    }
                }
            });

            this.view.onUpdate.throttle(() => this.update(), 100, this, this._namesapce);
        }

        protected increment(count: number): void {
            // this._view.scrollToIndex(this._view.first.index + this.clickCountToOffset(count));
            this.view.scrollTop += this.clickCountToOffset(count) * 100;
        }

        protected decrement(count: number): void {
            // this._view.scrollToIndex(this._view.first.index - this.clickCountToOffset(count));
            this.view.scrollTop -= this.clickCountToOffset(count) * 100;
        }

        private clickCountToOffset(count: number): number {
            if (count > 9)
                return 6;
            if (count > 6)
                return 4;
            if (count > 3)
                return 2;
            return 1;
        }

        protected dragStart(): void {
            this.updateLabel();
            super.dragStart();
        }

        protected drag(): void {
            this.updateLabel();
            super.drag();
        }

        protected dragEnd(): void {
            this.scrollToByThumb();
            this._label.classList.add("hidden");
            super.dragEnd();
        }

        private updateLabel(): void {
            if (this.labelProvider === null)
                return;
            var index = this.getIndex();
            var item = this.view.getItems()[index];
            this._label.classList.toggle("hidden", !Boolean(item));
            if (item && this._labelItem !== item) {
                this._labelItem = item;
                this._label.innerHTML = this.labelProvider(item.data);
            }

            if (item) {
                var margin = 10;
                var labelHeight = this._label.offsetHeight;
                var yPos = this.dragger.top;
                yPos += this.thumb.offsetHeight * 0.5;
                yPos -= labelHeight * 0.5;
                yPos += this.track.offsetTop;
                yPos = Calc.clampMax(yPos, this.element.offsetHeight - (labelHeight + margin));
                yPos = Calc.clampMin(yPos, margin);
                this._label.style.transform = `translateY(${yPos}px)`;
            }
        }

        private scrollToByThumb(): void {
            // if (this.end) {
            //     this.view.scrollToBottom();
            //     return;
            // }

            var index = this.getIndex();
            if (index < 0) {
                this.view.scrollToTop();
            } else if (index >= this.view.getItems().length) {
                this.view.scrollToBottom();
            }
            else {
                this.view.scrollToIndex(index);
            }
        }

        protected getIndex(): number {
            return this.behaviour.getIndex(this);
        }

        protected getPosition(): ScrollbarData {
            return this.behaviour.fromView(this);
        }
    }
}

namespace e5.ScrollbarBehaviour {
    class ScrollbarBehaviourIndex implements IScrollbarBehaviour {
        public getIndex(scrollbar: ScrollbarListDynamic): number {
            if (!scrollbar.view)
                return -1;

            if (scrollbar.view.getItems().length === 0) {
                if ((scrollbar.thumb.offsetHeight + scrollbar.dragger.top) > (scrollbar.track.offsetHeight - this.getMarginBottom(scrollbar)))
                    return 1;
                return -1;
            }

            var trackRange = scrollbar.track.offsetHeight - scrollbar.thumb.offsetHeight;
            var marginTop = this.getMarginTop(scrollbar);
            var marginBottom = this.getMarginBottom(scrollbar);
            var trackRangeMargin = trackRange - (marginTop + marginBottom);
            var ratio = Calc.clamp(scrollbar.dragger.top / trackRange, 0, 1);
            if (ratio < (marginTop / trackRange)) {
                //top
                return -1;
            }
            else if (ratio > ((trackRange - marginBottom) / trackRange)) {
                //bottom
                return scrollbar.view.getItems().length;
            }
            else {
                //content
                var maxIndex = scrollbar.view.getItems().length - 1;
                if (trackRangeMargin <= 0) {
                    return 0;
                } else {
                    var ratio = (scrollbar.dragger.top - marginTop) / trackRangeMargin;
                }
                var index = Calc.clamp(Math.round(ratio * maxIndex), 0, maxIndex);
                return index;
            }
        }

        public fromView(scrollbar: ScrollbarListDynamic): ScrollbarData {
            var items = scrollbar.view.getItems();

            var offsetTop = scrollbar.view.content.offsetTop;
            var scrollTop = scrollbar.view.scroller.scrollTop;

            var marginTop = this.getMarginTop(scrollbar);
            var marginBottom = this.getMarginBottom(scrollbar);

            var view = scrollbar.view;

            if (marginTop || marginBottom) {
                var yPos = 0;
                if (scrollTop < offsetTop) {
                    //top
                    yPos = (scrollTop / offsetTop) * marginTop;
                }
                else if (scrollTop > (offsetTop + view.content.offsetHeight)) {
                    //bottom
                    var offsetBottom = offsetTop + view.content.offsetHeight;
                    var bottomHeight = view.scroller.scrollHeight - offsetBottom;
                    var pixelsToScroll = bottomHeight - view.scroller.clientHeight;

                    if (pixelsToScroll <= 0) {
                        yPos = scrollbar.track.offsetHeight - scrollbar.thumb.offsetHeight;
                    }
                    else {
                        yPos = ((scrollTop - offsetBottom) / pixelsToScroll) * marginBottom;
                        yPos += scrollbar.track.offsetHeight - (marginBottom + scrollbar.thumb.offsetHeight);
                    }

                } else {
                    //content
                    var h = scrollbar.track.offsetHeight - (marginTop + marginBottom + scrollbar.thumb.offsetHeight);
                    var firstIndex = view.first ? view.first.index : 0;
                    var r = firstIndex / Calc.clampMin(items.length - 1, 1);
                    yPos = r * h + marginTop;
                }

                var ratio = yPos / (scrollbar.track.offsetHeight - scrollbar.thumb.offsetHeight);

                var scrollHeight = 1;
                var k = items.length > 0 ? 10 : 0;
                var minItemsCount = 1 / (1 - (marginTop + marginBottom + k) / scrollbar.track.offsetHeight);
                var clientHeight = 1 / Calc.clampMin(items.length, minItemsCount);
                return {
                    scrollHeight: scrollHeight,
                    scrollTop: ratio * (scrollHeight - clientHeight),
                    clientHeight: clientHeight
                };

            } else {
                var maxEntries = items.length;
                var firstIndex = view.first ? view.first.index : 0;
                return {
                    scrollHeight: maxEntries,
                    scrollTop: firstIndex,
                    clientHeight: 1
                };
            }
        }

        private getMarginTop(scrollbar: ScrollbarListDynamic): number {
            var offsetTop = scrollbar.view.content.offsetTop;
            if (offsetTop <= 0)
                return 0;
            return 10;
        }

        private getMarginBottom(scrollbar: ScrollbarListDynamic): number {
            var offsetTop = scrollbar.view.content.offsetTop;
            var offsetBottom = scrollbar.view.scroller.scrollHeight - (scrollbar.view.content.offsetHeight + offsetTop + scrollbar.view.scroller.offsetHeight);
            if (offsetBottom <= 0)
                return 0;
            return 10;
        }
    }

    export const INDEX = new ScrollbarBehaviourIndex();

    class ScrollbarBehaviourPureIndex implements IScrollbarBehaviour {
        public getIndex(scrollbar: ScrollbarListDynamic): number {
            var trackRange = scrollbar.track.offsetHeight - scrollbar.thumb.offsetHeight;
            var maxIndex = scrollbar.view.getItems().length - 1;
            var ratio = scrollbar.dragger.top / trackRange;
            var index = Calc.clamp(Math.round(ratio * maxIndex), 0, maxIndex);
            return index;
        }

        public fromView(scrollbar: ScrollbarListDynamic): ScrollbarData {
            var items = scrollbar.view.getItems();
            var maxEntries = items.length;
            var firstIndex = scrollbar.view.first ? scrollbar.view.first.index : 0;
            return {
                scrollHeight: maxEntries,
                scrollTop: firstIndex,
                clientHeight: 1
            };
        }
    }

    export const PURE_INDEX = new ScrollbarBehaviourPureIndex();

    class ScrollbarBehaviourDirect implements IScrollbarBehaviour {
        public getIndex(scrollbar: ScrollbarListDynamic): number {
            if (scrollbar.view.getItems().length === 0) {
                if ((scrollbar.thumb.offsetHeight + scrollbar.dragger.top) > (scrollbar.track.offsetHeight))
                    return 1;
                return -1;
            }

            var trackRange = scrollbar.track.offsetHeight - scrollbar.thumb.getBoundingClientRect().height;
            var ratio = Calc.clamp(scrollbar.dragger.top / trackRange, 0, 1);
            var scrollTop = Math.ceil(ratio * (scrollbar.view.scroller.scrollHeight - scrollbar.view.scroller.clientHeight));
            scrollTop -= scrollbar.view.content.offsetTop;

            var items = scrollbar.view.getItems();
            if (scrollTop < 0)
                return -1;

            for (var item of items) {
                if (item.offsetTop <= scrollTop && (item.offsetTop + item.height) > scrollTop) {
                    return item.index;
                }
            }

            return items.length;
        }

        public fromView(scrollbar: ScrollbarListDynamic): ScrollbarData {
            return {
                scrollHeight: scrollbar.view.scroller.scrollHeight,
                scrollTop: scrollbar.view.scroller.scrollTop,
                clientHeight: scrollbar.view.scroller.clientHeight
            };
        }
    }

    export const DIRECT = new ScrollbarBehaviourDirect();
}